import { Grid, useTheme } from '@simplywallst/ui-core';
import { Value, Term, TipContainer, ChartContainer, Growth } from './styled';
import useGlossaryTerms from '@/hooks/useGlossaryTerms';
import Tooltip from '@/components/Tooltip';
import DeferRenderUntilIntersecting from '@/components/DeferRenderUntilIntersecting';
import { shallowEqual, useSelector } from 'react-redux';
import { shouldSkipDefer } from '@/redux/ui/selectors/shouldSkipDefer';
import { selector } from '@/pages/CompanyReport/components/Valuation';
import loadable from '@loadable/component';

const DonutChart = loadable(() => import('@/components/DonutChart'), {
  resolveComponent: (component) => component.DonutChart,
});

interface Props {
  metricName: string;
  metricValue: string;
}

export const KeyMetricsChart = ({ metricName, metricValue }: Props) => {
  const getTerm = useGlossaryTerms();
  const multipleTerm = getTerm(`${metricName.toLowerCase()} ratio`);

  const selected = useSelector<
    Parameters<typeof selector>[0],
    ReturnType<typeof selector>
  >(selector, shallowEqual);

  const skipDefer = useSelector<
    Parameters<typeof shouldSkipDefer>[0],
    ReturnType<typeof shouldSkipDefer>
  >(shouldSkipDefer, shallowEqual);

  const theme = useTheme();

  return (
    <Grid
      templateRows="auto"
      templateColumns="auto 80px"
      templateColumnsSm="auto 280px"
      templateColumnsMd="auto 80px"
      templateColumnsLg="auto 200px"
    >
      <Grid alignContent="start" justifyContent="center">
        <DeferRenderUntilIntersecting skipDefer={skipDefer}>
          {() => (
            <ChartContainer data-cy-id="donut-chart-container">
              <DonutChart
                baseArc={{
                  label: 'Market cap',
                  formattedValue: selected.formattedMarketCap,
                  rawValue: selected.marketCap,
                }}
                innerArc={{
                  label: selected.metricsData[metricName]?.label || 'n/a',
                  formattedValue:
                    selected.metricsData[metricName]?.formattedValue || 'n/a',
                  rawValue: selected.metricsData[metricName]?.value || 0,
                  color: theme.color.chart01,
                }}
              />
            </ChartContainer>
          )}
        </DeferRenderUntilIntersecting>
      </Grid>
      <Grid alignContent="center" justifyContent="start">
        <Value data-cy-id="key-metric-value">{metricValue}</Value>

        <Tooltip
          openDelay={300}
          light
          title={<TipContainer>{multipleTerm}</TipContainer>}
        >
          <Term data-cy-id="key-metric-term">{`${metricName} Ratio`}</Term>
        </Tooltip>
        <Growth data-cy-id="key-metric-growth">
          {metricName === 'Forward PE' &&
            selected.metricsData[metricName].formattedGrowth !== 'n/a' &&
            `${selected.metricsData[metricName].formattedGrowth} earnings`}
          {metricName === 'Forward PS' &&
            selected.metricsData[metricName].formattedGrowth !== 'n/a' &&
            `${selected.metricsData[metricName].formattedGrowth} sales`}
        </Growth>
      </Grid>
    </Grid>
  );
};
